import { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { acquireToken } from './tokenRef';
import { redirectToLogin } from './redirect';

const WARNING_THRESHOLD_SECONDS = 120;
const PROACTIVE_REFRESH_AFTER_IDLE_MS = 5 * 60 * 1000;
const ACTIVITY_THROTTLE_MS = 1000;

/// <summary>
/// Mounted under AuthedShell, ahead of Routes. Tracks last user activity
/// (mousemove / keydown / touchstart / visibilitychange), reads the active
/// account's id-token expiry, and:
///   - Shows a non-dismissable countdown modal when expiry &lt; 120s.
///     [Stay signed in] -> acquireTokenSilent, [Sign out] -> logoutRedirect.
///   - Auto-redirects to login if the user ignores the modal past expiry.
///   - On tab return after >5min idle, proactively re-acquires before the
///     user clicks anything.
/// </summary>
export function SessionWatcher() {
  const { instance, accounts } = useMsal();
  const [remaining, setRemaining] = useState<number | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastActivityWriteRef = useRef<number>(0);

  // Activity tracking (throttled to 1 Hz so we're not writing on every
  // mousemove).
  useEffect(() => {
    const touch = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityWriteRef.current = now;
      lastActivityRef.current = now;
    };
    const opts: AddEventListenerOptions = { passive: true };
    window.addEventListener('mousemove', touch, opts);
    window.addEventListener('keydown', touch, opts);
    window.addEventListener('touchstart', touch, opts);
    return () => {
      window.removeEventListener('mousemove', touch);
      window.removeEventListener('keydown', touch);
      window.removeEventListener('touchstart', touch);
    };
  }, []);

  // Proactive refresh when the tab regains focus after a long idle.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const idle = Date.now() - lastActivityRef.current;
      const active = instance.getActiveAccount();
      if (active && idle > PROACTIVE_REFRESH_AFTER_IDLE_MS) {
        void acquireToken(active).catch(() => {
          void redirectToLogin();
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [instance]);

  // Expiry watcher.
  useEffect(() => {
    if (accounts.length === 0) {
      setRemaining(null);
      return;
    }
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      const active = instance.getActiveAccount() ?? accounts[0];
      const exp = (active?.idTokenClaims as { exp?: number } | undefined)?.exp;
      if (!exp) {
        setRemaining(null);
        return;
      }
      const secs = exp - Math.floor(Date.now() / 1000);
      if (secs <= 0) {
        // Token already expired and we somehow didn't redirect: try a silent
        // refresh, falling back to login redirect.
        setRemaining(null);
        void acquireToken(active).catch(() => {
          void redirectToLogin();
        });
        return;
      }
      setRemaining(secs <= WARNING_THRESHOLD_SECONDS ? secs : null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [accounts, instance]);

  if (remaining === null) return null;

  const onStay = async () => {
    const active = instance.getActiveAccount() ?? accounts[0];
    if (!active) {
      void redirectToLogin();
      return;
    }
    try {
      await acquireToken(active);
      setRemaining(null);
    } catch {
      void redirectToLogin();
    }
  };

  const onSignOut = () => {
    instance.logoutRedirect({ account: accounts[0] }).catch(console.error);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expiry-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60"
    >
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-[90%] p-6">
        <h2 id="session-expiry-title" className="text-lg font-semibold text-slate-900">
          Session expiring
        </h2>
        <p className="mt-2 text-sm text-slate-700">
          Your session expires in <span className="font-mono font-medium">{remaining}</span> seconds.
          Stay signed in to keep your work, or sign out now.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onSignOut}
            className="px-3 py-2 text-sm text-slate-700 hover:text-slate-900"
          >
            Sign out
          </button>
          <button
            type="button"
            onClick={onStay}
            className="px-4 py-2 text-sm bg-sky-600 text-white rounded hover:bg-sky-700"
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
